import { useState, useEffect } from "react";
import api from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Buildings, FileText, CurrencyDollar, ChartBar, Calculator, Bell, Warning, MagnifyingGlass, ArrowRight, Handshake } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const fmt = (n) => {
  if (!n || n === 0) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const piaLabels = {
  not_started: "Not Started",
  in_review: "In Review",
  info_requested: "Info Requested",
  approved: "Approved",
  denied: "Denied",
};
const piaColors = {
  not_started: "#6B7280",
  in_review: "#002FA7",
  info_requested: "#FFD100",
  approved: "#00B341",
  denied: "#FF2A00",
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [deadlines, setDeadlines] = useState([]);
  const [frnData, setFrnData] = useState(null);
  const [frnSearch, setFrnSearch] = useState("");
  const [frnYearFilter, setFrnYearFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const navigate = useNavigate();

  const loadData = () => {
    Promise.all([
      api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {}),
      api.get("/deadlines?limit=20").then((r) => setDeadlines(r.data)).catch(() => {}),
      api.get("/financials/frn-disbursements").then((r) => setFrnData(r.data)).catch(() => {}),
      api.get("/usac/sync/status").then((r) => setSyncStatus(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const triggerSync = async () => {
    setSyncing(true);
    try {
      await api.post("/usac/sync");
      // Poll for completion
      const poll = setInterval(async () => {
        try {
          const { data } = await api.get("/usac/sync/status");
          setSyncStatus(data);
          if (!data.running) {
            clearInterval(poll);
            setSyncing(false);
            loadData();
          }
        } catch { /* ignore */ }
      }, 5000);
    } catch {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-full">
        <p className="text-[#6B7280] text-sm">Loading dashboard...</p>
      </div>
    );
  }

  if (!stats) return null;

  const statCards = [
    { label: "Total Clients", value: stats.clients_count, icon: Buildings, onClick: () => navigate("/clients") },
    { label: "Active Contracts", value: stats.active_contracts, icon: FileText, onClick: () => navigate("/contracts") },
    { label: "Funding Requested", value: fmt(stats.total_funding_requested), icon: ChartBar, onClick: () => navigate("/forms/471") },
    { label: "Committed", value: fmt(stats.total_committed), icon: CurrencyDollar, onClick: () => navigate("/invoices") },
    { label: "Disbursed", value: fmt(stats.total_disbursed), icon: CurrencyDollar, onClick: () => navigate("/invoices") },
    { label: "Client Contracts", value: stats.client_contracts_count, icon: Handshake, onClick: () => navigate("/client-contracts") },
  ];

  const formCounts = [
    { label: "Form 470", count: stats.form470_count, path: "/forms/470" },
    { label: "Form 471", count: stats.form471_count, path: "/forms/471" },
    { label: "Form 486", count: stats.form486_count, path: "/forms/486" },
    { label: "BEAR (472)", count: stats.form472_count, path: "/invoices" },
    { label: "SPI (474)", count: stats.form474_count, path: "/invoices" },
  ];

  const piaData = Object.entries(stats.pia_breakdown || {}).map(([key, value]) => ({
    name: piaLabels[key] || key,
    value,
    color: piaColors[key] || "#6B7280",
  }));

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Overview</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-3">
          {syncStatus?.last_run && (
            <span className="text-xs text-[#6B7280]">
              Last sync: {new Date(syncStatus.last_run).toLocaleDateString()} {new Date(syncStatus.last_run).toLocaleTimeString()}
            </span>
          )}
          <button
            data-testid="usac-sync-btn"
            onClick={triggerSync}
            disabled={syncing}
            className="bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors"
          >
            <ArrowRight size={16} weight="bold" className={syncing ? "animate-spin" : ""} />
            {syncing ? "Syncing USAC..." : "Sync USAC Data"}
          </button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-0 border border-gray-200">
        {statCards.map((card, i) => {
          const Icon = card.icon;
          return (
            <button
              key={i}
              data-testid={`stat-card-${i}`}
              onClick={card.onClick}
              className="stat-card text-left border-r border-b border-gray-200 last:border-r-0 cursor-pointer"
            >
              <div className="flex items-center gap-2 mb-3">
                <Icon size={16} className="text-[#002FA7]" weight="bold" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{card.label}</span>
              </div>
              <p className="text-2xl font-bold text-[#1A1D20] tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                {card.value}
              </p>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Funding Pipeline Chart */}
        <div className="lg:col-span-2 bg-white border border-gray-200 p-6" data-testid="funding-chart">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">Funding Pipeline by Year</p>
          {stats.funding_by_year && stats.funding_by_year.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={stats.funding_by_year} barCategoryGap="20%">
                <XAxis dataKey="year" tick={{ fontSize: 12, fill: "#6B7280" }} axisLine={{ stroke: "#E5E7EB" }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#6B7280" }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v)} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ border: "1px solid #E5E7EB", borderRadius: 0, fontSize: 12 }} />
                <Bar dataKey="requested" fill="#002FA7" name="Requested" />
                <Bar dataKey="committed" fill="#00B341" name="Committed" />
                <Bar dataKey="disbursed" fill="#FFD100" name="Disbursed" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[280px] flex items-center justify-center text-sm text-[#6B7280]">
              No funding data yet. Add Form 471 applications to see the pipeline.
            </div>
          )}
        </div>

        {/* PIA Status Breakdown */}
        <div className="bg-white border border-gray-200 p-6" data-testid="pia-breakdown">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">PIA Inquiry Status</p>
          {piaData.length > 0 ? (
            <div className="space-y-3">
              {piaData.map((item, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-[#1A1D20]">{item.name}</span>
                  </div>
                  <span className="text-sm font-semibold text-[#1A1D20]">{item.value}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[200px] flex items-center justify-center text-sm text-[#6B7280]">
              No PIA data yet.
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Deadline Alerts */}
        <div className="bg-white border border-gray-200 p-6" data-testid="deadline-alerts">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Bell size={16} className="text-[#FF2A00]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280]">Deadline Alerts</p>
            </div>
            <button data-testid="view-all-deadlines" onClick={() => navigate("/deadlines")} className="text-xs text-[#002FA7] hover:underline font-medium">View All</button>
          </div>
          {deadlines.length === 0 ? (
            <div className="py-4 text-center text-sm text-[#6B7280]">No active deadlines</div>
          ) : (
            <div className="space-y-2">
              {deadlines.slice(0, 5).map((d, i) => {
                const urgColors = { overdue: "text-[#FF2A00]", urgent: "text-[#946B00]", upcoming: "text-[#002FA7]", future: "text-[#6B7280]" };
                const urgBg = { overdue: "bg-[#FF2A00]/5", urgent: "bg-[#FFD100]/5", upcoming: "bg-white", future: "bg-white" };
                return (
                  <div key={i} className={`flex items-center justify-between py-2 px-3 border border-gray-100 ${urgBg[d.urgency] || ""}`} data-testid={`dashboard-deadline-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#1A1D20] truncate">{d.title}</p>
                      <p className="text-xs text-[#6B7280] truncate">{d.client_name}</p>
                    </div>
                    <span className={`text-xs font-bold flex-shrink-0 ml-2 ${urgColors[d.urgency] || ""}`}>
                      {d.days_remaining < 0 ? `${Math.abs(d.days_remaining)}d late` : d.days_remaining === 0 ? "TODAY" : `${d.days_remaining}d`}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Form Counts */}
        <div className="bg-white border border-gray-200 p-6" data-testid="form-counts">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-4">E-Rate Forms Summary</p>
          <div className="space-y-2">
            {formCounts.map((fc, i) => (
              <button
                key={i}
                data-testid={`form-count-${i}`}
                onClick={() => navigate(fc.path)}
                className="w-full flex items-center justify-between py-2 px-3 border border-gray-100 hover:border-[#002FA7]/30 transition-colors text-left"
              >
                <span className="text-sm text-[#1A1D20]">{fc.label}</span>
                <span className="text-sm font-bold text-[#002FA7]">{fc.count}</span>
              </button>
            ))}
          </div>
        </div>

        {/* C2 Budget Summary */}
        <div className="bg-white border border-gray-200 p-6" data-testid="c2-budget-summary">
          <div className="flex items-center gap-2 mb-4">
            <Calculator size={16} className="text-[#002FA7]" weight="bold" />
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280]">C2 Budget Overview</p>
          </div>
          <div className="space-y-4">
            <div>
              <span className="text-sm text-[#6B7280]">Total Budget Cap</span>
              <p className="text-xl font-bold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(stats.total_c2_budget)}</p>
            </div>
            <div>
              <span className="text-sm text-[#6B7280]">Committed</span>
              <p className="text-xl font-bold text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{fmt(stats.total_c2_committed)}</p>
            </div>
            {stats.total_c2_budget > 0 && (
              <div>
                <div className="flex justify-between text-xs text-[#6B7280] mb-1">
                  <span>Utilization</span>
                  <span>{((stats.total_c2_committed / stats.total_c2_budget) * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100">
                  <div
                    className="h-full bg-[#002FA7]"
                    style={{ width: `${Math.min((stats.total_c2_committed / stats.total_c2_budget) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FRN Invoice Disbursements Lookup */}
      {frnData && (
        <div className="bg-white border border-gray-200 p-6" data-testid="frn-disbursements-widget">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <CurrencyDollar size={16} className="text-[#002FA7]" weight="bold" />
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280]">FRN Invoice & Disbursements</p>
            </div>
            <button data-testid="view-all-invoices" onClick={() => navigate("/invoices")} className="text-xs text-[#002FA7] hover:underline font-medium flex items-center gap-1">
              View All <ArrowRight size={12} />
            </button>
          </div>

          {/* Totals bar */}
          <div className="grid grid-cols-5 gap-0 border border-gray-200 mb-4">
            {[
              { label: "FRNs", value: frnData.totals.frn_count },
              { label: "Requested", value: fmt(frnData.totals.total_requested), color: "text-[#1A1D20]" },
              { label: "Committed", value: fmt(frnData.totals.total_committed), color: "text-[#002FA7]" },
              { label: "Invoiced", value: fmt(frnData.totals.total_invoiced), color: "text-[#D97706]" },
              { label: "Disbursed", value: fmt(frnData.totals.total_disbursed), color: "text-[#00B341]" },
            ].map((t, i) => (
              <div key={i} className="p-3 border-r border-gray-200 last:border-r-0">
                <span className="text-[9px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">{t.label}</span>
                <p className={`text-lg font-bold tracking-tight ${t.color || "text-[#1A1D20]"}`} style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{t.value}</p>
              </div>
            ))}
          </div>

          {/* Search + Year Filter */}
          <div className="flex items-center gap-3 mb-3">
            <div className="relative flex-1">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
              <Input data-testid="frn-search-input" placeholder="Search FRN, client, provider..." value={frnSearch} onChange={(e) => setFrnSearch(e.target.value)} className="rounded-none pl-9 h-8 text-xs" />
            </div>
            <Select value={frnYearFilter} onValueChange={setFrnYearFilter}>
              <SelectTrigger data-testid="frn-year-filter" className="rounded-none w-[120px] h-8 text-xs"><SelectValue placeholder="All Years" /></SelectTrigger>
              <SelectContent className="rounded-none">
                <SelectItem value="all">All Years</SelectItem>
                {[...new Set(frnData.rows.map((r) => r.funding_year).filter(Boolean))].sort().reverse().map((y) => (
                  <SelectItem key={y} value={y}>FY {y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="border border-gray-200 max-h-[340px] overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow className="data-table-header sticky top-0 z-10 bg-[#F8F9FA]">
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">FRN</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Client</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">FY</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Provider</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Requested</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Invoiced</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Disbursed</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">% Paid</TableHead>
                  <TableHead className="py-2 px-3 text-[10px] uppercase tracking-wider text-[#6B7280] font-semibold">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(() => {
                  let rows = frnData.rows;
                  if (frnYearFilter !== "all") rows = rows.filter((r) => r.funding_year === frnYearFilter);
                  if (frnSearch) rows = rows.filter((r) => [r.frn, r.client_name, r.service_provider].some((v) => v.toLowerCase().includes(frnSearch.toLowerCase())));
                  if (rows.length === 0) return (
                    <TableRow><TableCell colSpan={9} className="text-center py-6 text-[#6B7280] text-xs">No FRN records found</TableCell></TableRow>
                  );
                  return rows.slice(0, 50).map((r, idx) => (
                    <TableRow key={`${r.frn}-${idx}`} className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/frn-lifecycle?frn=${r.frn}`)} data-testid={`frn-disb-${r.frn}`}>
                      <TableCell className="py-2 px-3 font-mono text-xs font-bold text-[#002FA7]">{r.frn}</TableCell>
                      <TableCell className="py-2 px-3 text-xs text-[#1A1D20]">{r.client_name}</TableCell>
                      <TableCell className="py-2 px-3 text-xs">{r.funding_year}</TableCell>
                      <TableCell className="py-2 px-3 text-xs text-[#6B7280]">{r.service_provider}</TableCell>
                      <TableCell className="py-2 px-3 text-xs font-medium">{fmt(r.requested)}</TableCell>
                      <TableCell className="py-2 px-3 text-xs font-medium text-[#D97706]">{fmt(r.total_invoiced)}</TableCell>
                      <TableCell className="py-2 px-3 text-xs font-medium text-[#00B341]">{fmt(r.total_disbursed)}</TableCell>
                      <TableCell className="py-2 px-3">
                        <div className="flex items-center gap-1.5">
                          <div className="w-10 h-1.5 bg-gray-100"><div className="h-full bg-[#00B341]" style={{ width: `${Math.min(r.pct_disbursed, 100)}%` }} /></div>
                          <span className="text-[10px] text-[#6B7280]">{r.pct_disbursed}%</span>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 px-3">
                        <Badge className={`rounded-none text-[9px] border capitalize ${{
                          approved: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
                          committed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
                          draft: "bg-gray-100 text-[#6B7280] border-gray-200",
                          submitted: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
                          denied: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
                        }[r.status_471] || "bg-gray-100 text-[#6B7280] border-gray-200"}`}>{r.status_471}</Badge>
                      </TableCell>
                    </TableRow>
                  ));
                })()}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
}
